import * as Sentry from "@sentry/react";
import type { DBSchema, IDBPDatabase } from "idb";
import { openDB } from "idb";
import isEqual from "lodash/isEqual";
import { all, call, delay, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import type { RootState } from "../rootReducer";

import { saveTranscript } from "./actions";
import {
  AddStoryIndex,
  DeleteStoryIndex,
  LocalBackupFinishedLoading,
  SetHasUnsavedChanges,
  SetIndexNotes,
  SetIndexParagraph,
  SetLastSaved,
  SetStoryIndexMetadataActionCreator,
  SetStoryIndexTitleActionCreator,
  StoryLoaded,
  TranscriptUpdated,
} from "./reducer";

interface AuthoringData {
  id: string;
  timestamp: string;
  data: any;
}
interface AuthoringSchema extends DBSchema {
  transcripts: {
    key: string;
    value: AuthoringData;
  };
  indexes: {
    key: string;
    value: AuthoringData;
  };
}

const authoringDB = (): Promise<IDBPDatabase<AuthoringSchema>> => {
  const dbName = "theirstory-authoring";

  if (!("indexedDB" in window)) {
    return null;
  }
  return openDB<AuthoringSchema>(dbName, 2, {
    upgrade(db) {
      for (const [table, key] of [
        ["transcripts", "id"],
        ["indexes", "id"],
      ] as any) {
        if (!db.objectStoreNames.contains(table)) {
          db.createObjectStore(table, {
            keyPath: key,
          });
        }
      }
    },
  });
};

type StoryLoadedActionBody = ReturnType<typeof StoryLoaded>;

const debounceTime = 250;

// Deal with saving data to localstorage.
// Deep-compares changes to data returned by `selector` whenever any of the specified `actions` happen
// When a change happens, persist it, then delay by `debounceTime` until trying again
// Critically, after the debounce delay, check again to see if data has changed.
// If we didn't do that step, we'd drop any changes that happen during the debounce, unless you make another change after debouncing is done
function* handleChanges<T>(actions: string[], selector: (s: RootState) => T, persister: (data: T) => Promise<void>) {
  const dataContainer = { data: yield select(selector), onlyUpdate: false };

  while (true) {
    const newData = yield select(selector);

    if (!isEqual(newData, dataContainer.data)) {
      if (dataContainer.onlyUpdate) {
        dataContainer.onlyUpdate = false;
        dataContainer.data = newData;
      } else {
        yield put(SetHasUnsavedChanges(true));
        try {
          yield persister(newData);
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
        dataContainer.data = newData;
      }

      const { saved } = yield race({
        timeout: delay(debounceTime),
        saved: take(saveTranscript.pending),
      });

      if (saved) {
        // If a save happens during the debounce time
        // just update our internal representation of the tracked data,
        // but don't update the UI or anything
        dataContainer.onlyUpdate = true;
      }
      continue;
    }

    yield take((a) => actions.includes(a.type));
  }
}

function* autoSaveTranscript(storyId: string) {
  const idb = yield authoringDB();
  yield all([
    call(
      handleChanges,
      [
        SetIndexNotes.toString(),
        SetIndexParagraph.toString(),
        SetStoryIndexMetadataActionCreator.toString(),
        SetStoryIndexTitleActionCreator.toString(),
        AddStoryIndex.toString(),
        DeleteStoryIndex.toString(),
      ],
      (s: RootState) => s.storyAuthoring.items,
      async (data: any) => {
        await idb.put("indexes", {
          id: storyId,
          timestamp: `${new Date().getTime()}`,
          data: data,
        });
      }
    ),
    call(
      handleChanges,
      [TranscriptUpdated.toString()],
      (s: RootState) => s.storyAuthoring.transcriptSlate,
      async (data) => {
        await idb.put("transcripts", {
          id: storyId,
          timestamp: `${new Date().getTime()}`,
          data: data,
        });
      }
    ),
    takeEvery<ReturnType<typeof saveTranscript["fulfilled"]>>(saveTranscript.fulfilled, () =>
      Promise.all([idb.delete("indexes", storyId), idb.delete("transcripts", storyId)])
    ),
  ]);
}

function isValidDate(d) {
  return d instanceof Date && !isNaN(d.getTime());
}

function* tryRestoreAutosave({ story, transcriptUpdatedAt }: StoryLoadedActionBody["payload"]) {
  try {
    const storyId = story._id;
    const loadedIndexes = story.indexes;
    const latestIndexUpdated = loadedIndexes
      .map((idx) => new Date(idx.updated_at))
      .sort()
      .reverse()[0];

    // If we have both transcript and index in DB, pick the latest of their modification times
    // Otherwise, just pick whichever one we have
    const latestModified =
      isValidDate(transcriptUpdatedAt) && isValidDate(latestIndexUpdated)
        ? transcriptUpdatedAt > latestIndexUpdated
          ? transcriptUpdatedAt
          : latestIndexUpdated
        : (isValidDate(transcriptUpdatedAt) && transcriptUpdatedAt) ||
          (isValidDate(latestIndexUpdated) && latestIndexUpdated);

    const idb = yield authoringDB();
    const transcriptRecord = yield idb.get("transcripts", storyId);
    const indexRecord = yield idb.get("indexes", storyId);

    const savedTranscript = transcriptRecord?.data;
    const savedIndexes = indexRecord?.data;
    let anyRestored = false;

    // We only want to restore the transcript if the data in local storage is later
    // than any save that came from the server (i.e. if the user made and saved changes
    // which are more recent, possibly on a different computer, we will keep user's changes)
    if (savedTranscript && new Date(+transcriptRecord.timestamp) > latestModified) {
      yield put(
        TranscriptUpdated({
          transcriptSlate: savedTranscript,
        })
      );
      anyRestored = true;
    }

    if (savedIndexes) {
      for (const index of savedIndexes) {
        const idxId = index._id;
        const existingIndex = loadedIndexes.find((idx) => idx._id === idxId);

        // updated_at is ONLY changed server-side. The only case in which we DO NOT want to restore an
        // index from IDB is if the database has an index with a greater update timestamp than what we
        // have locally (which indicates the user made a subsequent save from a different location)
        if (!existingIndex?.updated_at || new Date(index.updated_at) >= new Date(existingIndex.updated_at)) {
          anyRestored = true;
          yield put(
            SetIndexNotes({
              indexId: idxId,
              notes: index.notes,
              isLoad: true,
            })
          );
          yield put(
            SetStoryIndexTitleActionCreator({
              indexId: idxId,
              title: index.title,
            })
          );
        }
      }
    }

    if (anyRestored) {
      yield put(SetLastSaved(latestModified));
      yield put(SetHasUnsavedChanges(true));
    }
  } catch (e) {
    Sentry.captureException(e);
    console.error(e);
  } finally {
    yield put(LocalBackupFinishedLoading());
  }
}

function* setupAutosave() {
  yield takeLatest<StoryLoadedActionBody>(StoryLoaded.toString(), function* ({ payload }) {
    const storyId = payload.story._id as unknown as string;
    yield tryRestoreAutosave(payload);
    yield autoSaveTranscript(storyId);
  });
}

export function* storyAuthoringSagas() {
  yield all([call(setupAutosave)]);
}
